import { Button } from "@/components/common/Button/Button"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { brandColors } from "@/lib/brandColors"
import { useEditResponsePair } from "@/services/api/questionnaireService"
import { EditResponsePairKey } from "@/types/hooks/useTextAreaStates"
import { QuestionnaireResponsePair } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { isLimitError } from "@/utils/limitErrors"
import { IconArrowBackUp, IconCheck, IconPencil } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import "react-loading-skeleton/dist/skeleton.css"
import { TextAreaListItem } from "../TextAreaListItem/TextAreaListItem"

interface QuestionItemProps {
  questionnaireId: string
  responsePair: QuestionnaireResponsePair
  editEnabled?: boolean
}

const EDIT_RESPONSE_PAIR_KEY: EditResponsePairKey = "question"

export const QuestionItem: React.FC<QuestionItemProps> = ({
  questionnaireId,
  responsePair,
  editEnabled = true,
}) => {
  const { translate } = useTranslation()
  const [isEditMode, setIsEditMode] = useState(false)
  const initialText = responsePair[EDIT_RESPONSE_PAIR_KEY] ?? ""
  const [text, setText] = useState(initialText)
  const [textRevertStorage, setTextRevertStorage] = useState<string | null>(
    null,
  )

  const {
    mutate: mutateResponsePair,
    isSuccess: isMutateResponsePairSuccess,
    isError: isMutateResponsePairError,
    error: mutateResponsePairError,
  } = useEditResponsePair()

  useEffect(() => {
    if (isMutateResponsePairError) {
      if (textRevertStorage) setText(textRevertStorage)

      // A limit or trial error already got the interceptor's upgrade toast
      if (!isLimitError(mutateResponsePairError)) {
        toast.error(
          translate("QUESTIONNAIRE_QUESTIONS/TOAST_EDIT_QUESTION_ERROR"),
        )
      }
    } else if (isMutateResponsePairSuccess) {
      toast.success(
        translate("QUESTIONNAIRE_QUESTIONS/TOAST_EDIT_QUESTION_SUCCESS"),
      )
    }
  }, [
    isMutateResponsePairError,
    mutateResponsePairError,
    isMutateResponsePairSuccess,
    textRevertStorage,
    translate,
  ])

  const handleActivateEditMode = () => {
    setTextRevertStorage(text)
    setIsEditMode(true)
  }

  const onTextChange = (changedText: string) => {
    setText(changedText)
  }

  const handleRevertButtonClick = () => {
    if (textRevertStorage) setText(textRevertStorage)
    setIsEditMode(false)
  }

  const handleSaveButtonClick = () => {
    mutateResponsePair({
      questionnaireId: questionnaireId,
      responsePairId: responsePair.id,
      question: text,
    })
    setIsEditMode(false)
  }

  return (
    <div className="py-S">
      {!isEditMode ? (
        <div
          className={classNames(
            "flex items-center justify-between",
            "px-S py-XS",
            "hover:bg-grey3",
            "rounded-lg",
          )}
        >
          <Typography variant="textM">{text}</Typography>
          {editEnabled ? (
            <button onClick={() => handleActivateEditMode()}>
              <IconPencil size={24} stroke={brandColors.grey5} />
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <TextAreaListItem
            text={text}
            onTextChange={onTextChange}
            responsePair={responsePair}
            editResponsePairKey={EDIT_RESPONSE_PAIR_KEY}
          />
          <div className="flex justify-end gap-XS">
            <Button
              variantColor="dark"
              label={translate("QUESTIONNAIRE_QUESTIONS/EDIT_REVERT")}
              onClick={() => handleRevertButtonClick()}
              IconRight={IconArrowBackUp}
              variantSize="small"
            />
            <Button
              variantColor="yellow"
              label={translate("QUESTIONNAIRE_QUESTIONS/EDIT_SAVE")}
              onClick={() => handleSaveButtonClick()}
              IconRight={IconCheck}
              variantSize="small"
            />
          </div>
        </>
      )}
    </div>
  )
}
